// DM-6: commands are pure serializable data, the ONLY way to change a Doc (ARC-3c).
// Each command carries everything needed to apply AND invert it (deleteEntities captures
// removed entities at creation time via makeDeleteCommand).
import type { AssetRef, Doc, Entity, GroupEntity, Layer } from "./entities";
import { newId } from "./document";
import { orphanPatches } from "./anchors";

export interface ZRef {
  layerId: string;
  index: number;
}

export type DocProps = Partial<
  Pick<Doc, "name" | "units" | "precision" | "defaultStyle" | "dimStyle" | "canvasGrid" | "sheet">
>;

export type Command =
  | { kind: "addEntities"; entities: Entity[]; z: ZRef[] }
  | { kind: "deleteEntities"; removed: { entity: Entity; z: ZRef }[] }
  | {
      kind: "updateEntities";
      patches: { id: string; before: Record<string, unknown>; after: Record<string, unknown> }[];
    }
  | { kind: "reorder"; moves: { id: string; from: ZRef; to: ZRef }[] }
  | { kind: "addLayer"; layer: Layer; index: number }
  | { kind: "deleteLayer"; layer: Layer; index: number }
  | { kind: "updateLayer"; id: string; before: Partial<Layer>; after: Partial<Layer> }
  | { kind: "reorderLayer"; id: string; from: number; to: number }
  | { kind: "group"; group: GroupEntity; z: ZRef; children: { id: string; z: ZRef }[] }
  | { kind: "ungroup"; group: GroupEntity; z: ZRef; children: { id: string; z: ZRef }[] }
  | { kind: "setDocProps"; before: DocProps; after: DocProps }
  // DM-6: asset REFS only — blobs live in IDB, never in history
  | { kind: "addAsset"; hash: string; ref: AssetRef }
  | { kind: "removeAsset"; hash: string; ref: AssetRef }
  | { kind: "batch"; label: string; commands: Command[] };

/** INV-2/INV-3: group top-level `ids` (captured z makes undo exact). Requires ≥ 2 ids. */
export function makeGroupCommand(doc: Doc, ids: string[], layerId: string): Command {
  if (ids.length < 2) throw new Error("commands: group needs at least 2 entities");
  // A part of a group must never go in a second group: deleting one group would then delete parts of the other
  // (2026-10-20, DP-PL-001: a lint pot lost its parts and the drawing did not open).
  const inGroup = new Set<string>();
  for (const e of Object.values(doc.entities)) if (e.type === "group") e.children.forEach((c) => inGroup.add(c));
  const children = ids.map((id) => {
    const entity = doc.entities[id];
    if (!entity) throw new Error(`commands: cannot group unknown entity ${id}`);
    if (inGroup.has(id)) throw new Error(`commands: ${id} is already part of a group`);
    const list = doc.zOrder[entity.layerId] ?? [];
    return { id, z: { layerId: entity.layerId, index: list.indexOf(id) } };
  });
  children.sort((a, b) => a.z.index - b.z.index);
  const group: GroupEntity = {
    id: newId(),
    type: "group",
    layerId,
    locked: false,
    hidden: false,
    children: children.map((c) => c.id)
  };
  return { kind: "group", group, z: { layerId, index: -1 }, children };
}

/** Dissolve a group: children return to their own layers' z-order (top). */
export function makeUngroupCommand(doc: Doc, groupId: string): Command {
  const group = doc.entities[groupId];
  if (!group || group.type !== "group") throw new Error(`commands: ${groupId} is not a group`);
  const list = doc.zOrder[group.layerId] ?? [];
  return {
    kind: "ungroup",
    group: structuredClone(group),
    z: { layerId: group.layerId, index: list.indexOf(groupId) },
    children: group.children.map((id) => ({
      id,
      z: { layerId: doc.entities[id]?.layerId ?? group.layerId, index: -1 }
    }))
  };
}

/**
 * Capture-at-creation helper so deleteEntities is invertible without doc snapshots.
 * INV-6: dimensions anchored to deleted entities are frozen to free anchors (+orphan
 * flag) in the SAME command, so undo restores associativity exactly.
 */
export function makeDeleteCommand(doc: Doc, ids: string[]): Command {
  // A deleted GROUP takes its parts with it (2026-10-06). Before this, only the group entity
  // went: its parts stayed in the drawing with no place in the z-order, could not be seen,
  // and the saved file then failed INV-1 on open ("missing from zOrder"). So each group is
  // ungrouped first (outer before inner: its parts re-enter the z-order), then the parts
  // are deleted. Undo replays it backwards: parts come back, then the groups re-form.
  const groups: string[] = [];
  const leaves: string[] = [];
  const seen = new Set<string>();
  const visit = (id: string): void => {
    if (seen.has(id)) return;
    seen.add(id);
    const entity = doc.entities[id];
    if (!entity) throw new Error(`commands: cannot delete unknown entity ${id}`);
    if (entity.type === "group") {
      groups.push(id);
      entity.children.forEach(visit);
    } else leaves.push(id);
  };
  ids.forEach(visit);

  const removed: { entity: Entity; z: ZRef }[] = leaves.map((id) => {
    const entity = doc.entities[id]!;
    const list = doc.zOrder[entity.layerId] ?? [];
    // a group's part is not in the z-order now; after the ungroup it is at the end (-1)
    return { entity: structuredClone(entity), z: { layerId: entity.layerId, index: list.indexOf(id) } };
  });
  const commands: Command[] = [];
  const orphans = orphanPatches(doc, seen);
  if (orphans.length > 0) commands.push({ kind: "updateEntities", patches: orphans });
  for (const g of groups) commands.push(makeUngroupCommand(doc, g));
  if (removed.length > 0) commands.push({ kind: "deleteEntities", removed });
  if (commands.length === 1) return commands[0]!;
  return { kind: "batch", label: "delete", commands };
}

/**
 * Delete children FROM INSIDE a group (edit-in-place). A plain delete would leave the
 * group's children array dangling and undo would resurrect the child top-level, so:
 * ungroup (children re-enter z-order) → delete the ids → re-group the survivors under
 * the SAME group entity. Fewer than 2 survivors dissolve the group (INV: groups ≥ 2).
 * One undoable batch; the inverse replays exactly.
 */
export function makeDeleteChildrenCommand(doc: Doc, groupId: string, ids: string[]): Command {
  const group = doc.entities[groupId];
  if (!group || group.type !== "group") throw new Error(`commands: ${groupId} is not a group`);
  const doomed = new Set(ids.filter((id) => group.children.includes(id)));
  if (doomed.size === 0) throw new Error("commands: nothing to delete inside the group");

  const ungroup = makeUngroupCommand(doc, groupId);
  const list = doc.zOrder[group.layerId] ?? [];
  const groupZ: ZRef = { layerId: group.layerId, index: list.indexOf(groupId) };

  // The doomed children are not in the z-order yet; after the ungroup step they are (at the
  // end). makeDeleteCommand captures them with index -1 and also takes the parts of any
  // doomed sub-group with it, so nothing is left behind outside the z-order (INV-1).
  const survivors = group.children.filter((id) => !doomed.has(id));
  const commands: Command[] = [ungroup, makeDeleteCommand(doc, [...doomed])];
  if (survivors.length >= 2) {
    commands.push({
      kind: "group",
      group: { ...structuredClone(group), children: survivors } as GroupEntity,
      z: groupZ,
      children: survivors.map((id) => ({
        id,
        z: { layerId: doc.entities[id]?.layerId ?? group.layerId, index: -1 }
      }))
    });
  }
  return { kind: "batch", label: "delete", commands };
}

function insertZ(doc: Doc, id: string, z: ZRef): void {
  const list = doc.zOrder[z.layerId];
  if (!list) throw new Error(`commands: unknown layer ${z.layerId}`);
  const index = z.index < 0 || z.index > list.length ? list.length : z.index;
  list.splice(index, 0, id);
}

function removeZ(doc: Doc, id: string, layerId: string): void {
  const list = doc.zOrder[layerId];
  if (!list) throw new Error(`commands: unknown layer ${layerId}`);
  const i = list.indexOf(id);
  if (i >= 0) list.splice(i, 1);
}

/** Applies `cmd` to `doc` in place. Atomic: validates references before mutating. */
export function applyCommand(doc: Doc, cmd: Command): void {
  switch (cmd.kind) {
    case "addEntities": {
      if (cmd.entities.length !== cmd.z.length) throw new Error("commands: entities/z length mismatch");
      cmd.z.forEach((z) => {
        if (!doc.zOrder[z.layerId]) throw new Error(`commands: unknown layer ${z.layerId}`);
      });
      cmd.entities.forEach((entity, i) => {
        doc.entities[entity.id] = structuredClone(entity);
        insertZ(doc, entity.id, cmd.z[i]!);
      });
      break;
    }
    case "deleteEntities": {
      for (const { entity } of cmd.removed) {
        if (!doc.entities[entity.id]) throw new Error(`commands: delete of unknown entity ${entity.id}`);
      }
      for (const { entity, z } of cmd.removed) {
        removeZ(doc, entity.id, z.layerId);
        delete doc.entities[entity.id];
      }
      break;
    }
    case "updateEntities": {
      for (const patch of cmd.patches) {
        if (!doc.entities[patch.id]) throw new Error(`commands: update of unknown entity ${patch.id}`);
      }
      for (const patch of cmd.patches) {
        Object.assign(doc.entities[patch.id]!, structuredClone(patch.after));
      }
      break;
    }
    case "reorder": {
      for (const move of cmd.moves) {
        removeZ(doc, move.id, move.from.layerId);
        insertZ(doc, move.id, move.to);
        doc.entities[move.id]!.layerId = move.to.layerId;
      }
      break;
    }
    case "addLayer": {
      doc.layers.splice(cmd.index, 0, structuredClone(cmd.layer));
      doc.zOrder[cmd.layer.id] = [];
      break;
    }
    case "deleteLayer": {
      const list = doc.zOrder[cmd.layer.id] ?? [];
      if (list.length > 0) throw new Error("commands: deleteLayer requires an empty layer (migrate first)");
      doc.layers = doc.layers.filter((l) => l.id !== cmd.layer.id);
      delete doc.zOrder[cmd.layer.id];
      break;
    }
    case "updateLayer": {
      const layer = doc.layers.find((l) => l.id === cmd.id);
      if (!layer) throw new Error(`commands: unknown layer ${cmd.id}`);
      Object.assign(layer, structuredClone(cmd.after));
      break;
    }
    case "reorderLayer": {
      const from = doc.layers.findIndex((l) => l.id === cmd.id);
      if (from < 0) throw new Error(`commands: unknown layer ${cmd.id}`);
      const [layer] = doc.layers.splice(from, 1);
      doc.layers.splice(Math.min(cmd.to, doc.layers.length), 0, layer!);
      break;
    }
    case "group": {
      for (const c of cmd.children) {
        if (!doc.entities[c.id]) throw new Error(`commands: group child ${c.id} missing`);
      }
      for (const c of cmd.children) removeZ(doc, c.id, c.z.layerId);
      doc.entities[cmd.group.id] = structuredClone(cmd.group);
      insertZ(doc, cmd.group.id, cmd.z);
      break;
    }
    case "ungroup": {
      if (!doc.entities[cmd.group.id]) throw new Error(`commands: unknown group ${cmd.group.id}`);
      removeZ(doc, cmd.group.id, cmd.z.layerId);
      delete doc.entities[cmd.group.id];
      for (const c of [...cmd.children].sort((a, b) => a.z.index - b.z.index)) {
        insertZ(doc, c.id, c.z);
      }
      break;
    }
    case "setDocProps": {
      Object.assign(doc, structuredClone(cmd.after));
      break;
    }
    case "addAsset": {
      doc.assets[cmd.hash] = structuredClone(cmd.ref);
      break;
    }
    case "removeAsset": {
      delete doc.assets[cmd.hash];
      break;
    }
    case "batch": {
      for (const c of cmd.commands) applyCommand(doc, c);
      break;
    }
  }
  doc.modifiedAt = new Date().toISOString();
}

/** Exact inverse per DM-6. */
export function invertCommand(cmd: Command): Command {
  switch (cmd.kind) {
    case "addEntities":
      return {
        kind: "deleteEntities",
        removed: cmd.entities.map((entity, i) => ({ entity, z: cmd.z[i]! }))
      };
    case "deleteEntities":
      return {
        kind: "addEntities",
        entities: cmd.removed.map((r) => r.entity),
        z: cmd.removed.map((r) => r.z)
      };
    case "updateEntities":
      return {
        kind: "updateEntities",
        patches: cmd.patches.map((p) => ({ id: p.id, before: p.after, after: p.before }))
      };
    case "reorder":
      return { kind: "reorder", moves: cmd.moves.map((m) => ({ id: m.id, from: m.to, to: m.from })) };
    case "addLayer":
      return { kind: "deleteLayer", layer: cmd.layer, index: cmd.index };
    case "deleteLayer":
      return { kind: "addLayer", layer: cmd.layer, index: cmd.index };
    case "updateLayer":
      return { kind: "updateLayer", id: cmd.id, before: cmd.after, after: cmd.before };
    case "reorderLayer":
      return { kind: "reorderLayer", id: cmd.id, from: cmd.to, to: cmd.from };
    case "group":
      return { kind: "ungroup", group: cmd.group, z: cmd.z, children: cmd.children };
    case "ungroup":
      return { kind: "group", group: cmd.group, z: cmd.z, children: cmd.children };
    case "setDocProps":
      return { kind: "setDocProps", before: cmd.after, after: cmd.before };
    case "addAsset":
      return { kind: "removeAsset", hash: cmd.hash, ref: cmd.ref };
    case "removeAsset":
      return { kind: "addAsset", hash: cmd.hash, ref: cmd.ref };
    case "batch":
      return {
        kind: "batch",
        label: cmd.label,
        commands: [...cmd.commands].reverse().map(invertCommand)
      };
  }
}
